import * as React from 'react';

import Edit from './edit';
import { isAnEditRegex, negativeMatchRegex, startsWithPunctuationRegex, endsWithOpeningPunctuationRegex } from './sharedRegexes';

import {
  MULTIPLE_UNNECESSARY_ADDITION,
  MULTIPLE_UNNECESSARY_DELETION,
  SINGLE_UNNECESSARY_ADDITION,
  SINGLE_UNNECESSARY_DELETION,
  UNNECESSARY_CHANGE,
  UNNECESSARY_SPACE
} from '../../helpers/determineUnnecessaryEditType';
import { Concept } from '../../interfaces/concepts';
import { ConceptResultObject } from '../../interfaces/proofreaderActivities';

const unnecessaryArray = [UNNECESSARY_SPACE, MULTIPLE_UNNECESSARY_DELETION, SINGLE_UNNECESSARY_DELETION, MULTIPLE_UNNECESSARY_ADDITION, SINGLE_UNNECESSARY_ADDITION, UNNECESSARY_CHANGE]

interface PassageReviewerProps {
  text: string;
  concepts: Concept[];
  conceptResultsObjects?: ConceptResultObject[];
  finishReview: Function;
}

interface PassageReviewerState {
  activeIndex: number;
  numberOfEdits: number;
}

export default class PassageReviewer extends React.Component<PassageReviewerProps, PassageReviewerState> {
  constructor(props: PassageReviewerProps) {
    super(props)

    const matches = props.text ? props.text.match(isAnEditRegex) : []
    const numberOfEdits = matches ? matches.length : 0

    this.state = {
      activeIndex: 0,
      numberOfEdits
    }
  }

  componentDidMount() {
    this.scrollToActiveIndex()
  }

  next = () => {
    const { activeIndex, numberOfEdits } = this.state
    const { finishReview, } = this.props
    if (activeIndex + 1 === numberOfEdits) {
      finishReview()
    } else {
      this.setState(prevState => ({ activeIndex: prevState.activeIndex + 1}), this.scrollToActiveIndex)
    }
  }

  back = () => {
    const { activeIndex, } = this.state
    if (activeIndex === 0) { return }

    this.setState(prevState => ({ activeIndex: prevState.activeIndex - 1}), this.scrollToActiveIndex)
  }

  scrollToActiveIndex() {
    const { activeIndex, } = this.state
    const el = document.getElementById(String(activeIndex))
    if (el) {
      window.scrollTo(0, window.pageYOffset + el.getBoundingClientRect().top - 34)
    }
  }

  renderFormattedText() {
    const { text, concepts, conceptResultsObjects } = this.props
    const paragraphs = text.split('</p><p>')
    const { activeIndex, numberOfEdits } = this.state
    let index = 0
    // Track the index for necessary edits to match with conceptResultsObjects
    let necessaryEditIndex = 0

    return paragraphs.map((paragraph: string, paragraphIndex: number) => {
      const parts: Array<string|JSX.Element> = paragraph.replace(/<p>|<\/p>/g, '').split(/{|}/g)
      for (let i = 0; i < parts.length; i +=1) {
        if (typeof parts[i] === "string" && parts[i][0] === '+') {
          const plusMatch = parts[i].match(/\+([^-]+)-/m)
          const plus = plusMatch ? plusMatch[1] : ''
          const conceptUIDMatch = parts[i].match(/\|(.+)/m)
          const conceptUID = conceptUIDMatch ? conceptUIDMatch[1] : ''
          const negativeMatch = parts[i].match(negativeMatchRegex)
          const negative = negativeMatch ? negativeMatch[1] : null
          const concept = concepts.find(c => c.uid === conceptUID)
          const indexToPass = index
          let state = 'correct'
          const isUnnecessaryEdit = unnecessaryArray.includes(conceptUID)
          if (isUnnecessaryEdit) {
            state = conceptUID
          } else if (negative) {
            state = 'incorrect'
          }

          // Get the sentence context from conceptResultsObjects for necessary edits
          // The prompt is already computed in checkWork and stored in conceptResultsObjects
          let sentenceContext: string | undefined
          if (!isUnnecessaryEdit && conceptResultsObjects && conceptResultsObjects[necessaryEditIndex]) {
            sentenceContext = conceptResultsObjects[necessaryEditIndex].metadata.prompt
            necessaryEditIndex += 1
          }

          // Check spacing rules for surrounding text
          const previousText = typeof parts[i - 1] === 'string' ? parts[i - 1] : ''
          const nextText = typeof parts[i + 1] === 'string' ? parts[i + 1] : ''
          // Trim whitespace from the end of previous text to check for opening punctuation
          const previousTextEndsWithOpeningPunctuation = endsWithOpeningPunctuationRegex.test(previousText.trimEnd())
          const nextTextStartsWithPunctuation = startsWithPunctuationRegex.test(nextText)

          // Remove trailing space from previous text if it ends with opening punctuation
          if (previousTextEndsWithOpeningPunctuation && i > 0 && typeof parts[i - 1] === 'string') {
            parts[i - 1] = previousText.trimEnd()
          }

          // Remove leading space from next text if it starts with closing punctuation
          if (nextTextStartsWithPunctuation && typeof parts[i + 1] === 'string') {
            parts[i + 1] = nextText.trimStart()
          }

          index+=1
          parts[i] = (<Edit
            activeIndex={activeIndex}
            back={indexToPass ? this.back : null}
            concept={concept}
            displayText={plus}
            id={`${indexToPass}`}
            incorrectText={negative}
            index={indexToPass}
            next={this.next}
            nextTextStartsWithPunctuation={nextTextStartsWithPunctuation}
            numberOfEdits={numberOfEdits}
            previousTextEndsWithOpeningPunctuation={previousTextEndsWithOpeningPunctuation}
            sentenceContext={sentenceContext}
            state={state}
          />)
        }
      }
      return <p key={paragraphIndex}>{parts}</p>
    })
  }

  render() {
    const { text, } = this.props

    if (text) {
      return (
        <div className="reviewer-container">
          <div className="reviewer" >
            {this.renderFormattedText()}
          </div>
        </div>
      )
    } else {
      return <p>No passage</p>
    }
  }

}
